/**
 * Integrações (redesign admin, Fase 5): visão geral e detalhe por integração
 * sobre o MESMO registry de `/configuracoes` (`useIntegrations`). Somente
 * leitura e sem valor de segredo — só o nome da variável e se está configurada.
 * A página `/configuracoes` continua sendo a vista completa com a tabela de
 * segredos de todas as integrações.
 */
import { createElement as h, type ReactElement } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Plug, Power, TriangleAlert } from "lucide-react";
import { StatusPanel, errorDetail } from "../components/StatusPanel.js";
import type { PlatformIntegrationStatus } from "../api/types.js";
import { formatCount } from "../format.js";
import { useIntegrations } from "../queries.js";
import { Card, CardHeader, EmptyState, PageHeader, StatTile } from "../ui/index.js";

const STATE_LABELS: Record<PlatformIntegrationStatus["state"], string> = { real: "Real", simulator: "Simulador" };

function stateBadge(state: PlatformIntegrationStatus["state"]): ReactElement {
  const real = state === "real";
  return h(
    "span",
    {
      "data-integration-state": state,
      className:
        "inline-flex items-center gap-1.5 rounded-pill border bg-surface px-2 py-0.5 text-xs font-semibold " +
        (real ? "border-success text-success" : "border-warning text-warning")
    },
    h(real ? CheckCircle2 : TriangleAlert, { className: "h-3.5 w-3.5", "aria-hidden": true, focusable: false }),
    STATE_LABELS[state]
  );
}

const NOT_PUBLISHED = h(StatusPanel, {
  kind: "awaiting_source",
  title: "Configuração de integrações não publicada neste ambiente",
  detail: "A rota /api/platform/config/integrations não respondeu aqui."
});

export function IntegracoesView({ integrations }: { integrations: PlatformIntegrationStatus[] }): ReactElement {
  const real = integrations.filter((i) => i.state === "real").length;
  const missing = integrations.reduce((sum, i) => sum + i.missing.length, 0);
  return h(
    "div",
    { className: "page page--integracoes flex flex-col gap-6", "data-page": "integracoes" },
    h(PageHeader, { title: "Integrações", description: "Estado de cada integração e o que falta para sair do simulador." }),
    h(
      "section",
      { "aria-label": "Resumo das integrações", className: "grid grid-cols-1 gap-4 sm:grid-cols-3" },
      h(StatTile, { label: "Integrações", value: formatCount(integrations.length), icon: Plug, "data-kpi": "int-total" }),
      h(StatTile, { label: "Em produção (real)", value: formatCount(real), icon: CheckCircle2, "data-kpi": "int-real" }),
      h(StatTile, { label: "Segredos faltando", value: formatCount(missing), icon: TriangleAlert, "data-kpi": "int-faltando" })
    ),
    integrations.length === 0
      ? h(EmptyState, { title: "Nenhuma integração registrada" })
      : h(
          "div",
          { className: "grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" },
          integrations.map((integration) =>
            h(
              Card,
              { key: integration.id, "data-integration": integration.id },
              h(CardHeader, { title: integration.name, actions: stateBadge(integration.state) }),
              h(
                "p",
                { className: "m-0 flex items-center gap-2 text-sm text-muted" },
                h(Power, { className: "h-4 w-4", "aria-hidden": true, focusable: false }),
                `Flag ${integration.flag.name}: ${integration.flag.enabled ? "ligado" : "desligado"}`
              ),
              h(
                "p",
                { className: "mt-1 text-sm text-muted" },
                integration.missing.length === 0
                  ? "Todos os segredos configurados."
                  : `${formatCount(integration.missing.length)} segredo(s) faltando.`
              ),
              h(Link, { to: `/integracoes/${integration.id}`, className: "mt-3 inline-block text-sm font-semibold text-brand-text" }, "Ver detalhe")
            )
          )
        )
  );
}

export function IntegracaoDetalheView({ integration }: { integration: PlatformIntegrationStatus | undefined }): ReactElement {
  if (!integration) {
    return h(
      "div",
      { className: "page page--integracao-detalhe flex flex-col gap-6", "data-page": "integracao-detalhe" },
      h(PageHeader, { title: "Integração" }),
      h(StatusPanel, { kind: "empty", title: "Integração não encontrada" }),
      h(Link, { to: "/integracoes", className: "text-sm font-semibold text-brand-text" }, "Voltar às integrações")
    );
  }
  return h(
    "div",
    { className: "page page--integracao-detalhe flex flex-col gap-6", "data-page": "integracao-detalhe", "data-integration": integration.id },
    h(PageHeader, {
      title: integration.name,
      description: integration.state === "real" ? "Credenciais completas: o adapter real é usado." : "Falta credencial: o adapter simulado responde.",
      actions: stateBadge(integration.state)
    }),
    h(
      Card,
      null,
      h(CardHeader, { title: "Segredos exigidos", description: "Nome da variável e situação; o valor nunca é exibido." }),
      integration.secrets.length === 0
        ? h(EmptyState, { title: "Nenhum segredo exigido" })
        : h(
            "ul",
            { className: "m-0 flex list-none flex-col divide-y divide-border p-0" },
            integration.secrets.map((secret) =>
              h(
                "li",
                { key: secret.name, className: "flex items-center justify-between gap-3 py-2 text-sm", "data-secret-configured": secret.configured ? "yes" : "no" },
                h("code", { className: "text-ink" }, secret.name),
                h("span", { className: secret.configured ? "font-semibold text-success" : "font-semibold text-warning" }, secret.configured ? "configurado" : "faltando")
              )
            )
          )
    ),
    h(
      Card,
      null,
      h(CardHeader, { title: "Flag de ativação" }),
      h(
        "p",
        { className: "m-0 text-sm text-muted", "data-flag-enabled": integration.flag.enabled ? "on" : "off" },
        `${integration.flag.name} está ${integration.flag.enabled ? "ligado" : "desligado"}. Para ativar, defina ${integration.flag.name}=on no ambiente e faça o redeploy (informativo: esta tela não altera o ambiente).`
      )
    ),
    h(Link, { to: "/integracoes", className: "text-sm font-semibold text-brand-text" }, "Voltar às integrações")
  );
}

export function Integracoes(): ReactElement {
  const query = useIntegrations();
  if (query.isPending) return h(StatusPanel, { kind: "loading", title: "Carregando integrações…" });
  if (query.error) return h(StatusPanel, { kind: "error", title: "Não foi possível carregar as integrações", detail: errorDetail(query.error) });
  if (!query.data) return NOT_PUBLISHED;
  return h(IntegracoesView, { integrations: query.data });
}

export function IntegracaoDetalhe(): ReactElement {
  const { key } = useParams();
  const query = useIntegrations();
  if (query.isPending) return h(StatusPanel, { kind: "loading", title: "Carregando integração…" });
  if (query.error) return h(StatusPanel, { kind: "error", title: "Não foi possível carregar a integração", detail: errorDetail(query.error) });
  if (!query.data) return NOT_PUBLISHED;
  return h(IntegracaoDetalheView, { integration: query.data.find((i) => i.id === key) });
}
